/** Country code → display info. */
export const COUNTRY_INFO: Record<string, { name: string; flag: string }> = {
  IN: { name: 'India', flag: '🇮🇳' }, US: { name: 'United States', flag: '🇺🇸' },
  AE: { name: 'UAE', flag: '🇦🇪' }, GB: { name: 'United Kingdom', flag: '🇬🇧' },
  CA: { name: 'Canada', flag: '🇨🇦' }, AU: { name: 'Australia', flag: '🇦🇺' },
  PK: { name: 'Pakistan', flag: '🇵🇰' }, BD: { name: 'Bangladesh', flag: '🇧🇩' },
  SG: { name: 'Singapore', flag: '🇸🇬' }, MY: { name: 'Malaysia', flag: '🇲🇾' },
  ZA: { name: 'South Africa', flag: '🇿🇦' }, NG: { name: 'Nigeria', flag: '🇳🇬' },
  DE: { name: 'Germany', flag: '🇩🇪' }, FR: { name: 'France', flag: '🇫🇷' },
  BR: { name: 'Brazil', flag: '🇧🇷' }, NZ: { name: 'New Zealand', flag: '🇳🇿' },
  NL: { name: 'Netherlands', flag: '🇳🇱' }, JP: { name: 'Japan', flag: '🇯🇵' },
  PH: { name: 'Philippines', flag: '🇵🇭' }, ID: { name: 'Indonesia', flag: '🇮🇩' },
  SA: { name: 'Saudi Arabia', flag: '🇸🇦' }, QA: { name: 'Qatar', flag: '🇶🇦' },
  KW: { name: 'Kuwait', flag: '🇰🇼' }, LK: { name: 'Sri Lanka', flag: '🇱🇰' },
};

// Keyed by Phyllo's actual follower_types names (real / suspicious / influencers
// / mass_followers). Each type gets a distinct semantic color.
// Colors match Figma "Followers Quality" donut (node 2019:69284): Real People =
// Brand/500 purple, Mass Subscribers = teal, Suspicious = pink, Influencers =
// light Brand purple.
export const FOLLOWER_TYPE_META: Record<string, { label: string; color: string; sub: string }> = {
  real: { label: 'Real People', color: '#6C09F7', sub: 'Engaged, authentic users with typical behavior.' },
  mass_followers: { label: 'Mass Subscribers', color: '#1BBFA6', sub: 'Follow 1500+ accounts; broad exposure.' },
  influencers: { label: 'Influencers', color: '#B89CF5', sub: 'Accounts with 1K+ followers; moderate reach.' },
  suspicious: { label: 'Suspicious Account', color: '#E0508A', sub: 'Unreliable or non-credible; low trust signals.' },
};

// Phyllo's follower-type keys vary across API versions; map alternates onto the
// canonical keys above so the semantic colors always apply.
const FOLLOWER_TYPE_ALIASES: Record<string, string> = {
  real_people: 'real',
  suspicious_accounts: 'suspicious',
  influencer: 'influencers',
  mass: 'mass_followers',
  mass_subscribers: 'mass_followers',
};

// Distinct palette for any follower type not in the meta — assigned by index so
// multiple unknown types never collide on one color (the old fallback was a
// single gray, which made every unmatched type look identical). Chosen to not
// clash with the four semantic colors above.
const FOLLOWER_TYPE_FALLBACK = ['#a855f7', '#0ea5e9', '#ec4899', '#14b8a6', '#f97316', '#84cc16'];

/** "mass_followers" → "Mass Followers" for any type without meta. */
function prettyFollowerType(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// InsightIQ's raw bucket strings use a leading dash for "less than" and a
// trailing dash for "and above" (e.g. "-500", "1500-", "65-") — readable as
// range notation, but reads as a negative number/typo rendered verbatim in
// a chart.
function formatOpenEndedRange(range: string): string {
  if (range.startsWith('-')) return `<${range.slice(1)}`;
  if (range.endsWith('-')) return `${range.slice(0, -1)}+`;
  return range;
}

export function buildAgeGender(dist: { gender: string; age_range: string; value: number }[]) {
  if (!dist?.length) return null;
  const byAge: Record<string, { male: number; female: number }> = {};
  for (const d of dist) {
    const bucket = byAge[d.age_range] ?? { male: 0, female: 0 };
    byAge[d.age_range] = bucket;
    if (d.gender?.toUpperCase() === 'MALE') bucket.male = Math.round(d.value * 100) / 100;
    else bucket.female = Math.round(d.value * 100) / 100;
  }
  return Object.entries(byAge).sort(([a], [b]) => a.localeCompare(b))
    .map(([label, v]) => ({ label: formatOpenEndedRange(label), male: v.male, female: v.female }));
}

export function buildCountries(countries: { code?: string; name?: string; value: number }[]) {
  if (!countries?.length) return null;
  return countries.slice(0, 5).map(c => {
    const info = c.code ? (COUNTRY_INFO[c.code] ?? { name: c.code, flag: '🌐' }) : { name: c.name ?? '—', flag: '🌐' };
    return { label: info.name, val: Math.round(c.value * 100) / 100, code: c.code ?? '' };
  });
}

export function buildFollowerTypes(types: { name?: string; type?: string; value: number }[]) {
  if (!types?.length) return null;

  // Phyllo returns per-demographic slices (age × gender) per type.
  // Aggregate all slices with the same key into a single total.
  const totals = new Map<string, number>();
  for (const t of types) {
    const key = (t.name ?? t.type ?? '').toLowerCase().replace(/ /g, '_');
    if (!key) continue;
    totals.set(key, (totals.get(key) ?? 0) + t.value);
  }

  if (totals.size === 0) return null;

  return Array.from(totals.entries())
    .sort(([, a], [, b]) => b - a)
    .map(([rawKey, value], i) => {
      const key = FOLLOWER_TYPE_ALIASES[rawKey] ?? rawKey;
      const meta = FOLLOWER_TYPE_META[key];
      // Known type → its semantic color; unknown → a distinct palette color by
      // index so no two options ever share one.
      const fallback = FOLLOWER_TYPE_FALLBACK[i % FOLLOWER_TYPE_FALLBACK.length] ?? FOLLOWER_TYPE_FALLBACK[0] ?? '#a855f7';
      return {
        label: meta?.label ?? prettyFollowerType(rawKey),
        pct: `${Math.round(value)}%`,
        pctNum: Math.round(value),
        color: meta?.color ?? fallback,
        sub: meta?.sub ?? '',
      };
    });
}

export function buildInterests(interests: { name: string; value: number }[]) {
  if (!interests?.length) return null;
  const mapped = [...interests].sort((a, b) => b.value - a.value)
    .map(i => ({ label: i.name, val: Math.round(i.value * 10) / 10 }));
  const mid = Math.ceil(mapped.length / 2);
  return { col1: mapped.slice(0, mid), col2: mapped.slice(mid) };
}

export function buildCities(cities: { name: string; value: number }[]) {
  if (!cities?.length) return null;
  return cities.slice(0, 5).map(x => ({ label: x.name, val: Math.round(x.value * 100) / 100 }));
}

export function buildStates(states: { name: string; value: number }[]) {
  if (!states?.length) return null;
  return states.slice(0, 5).map(x => ({ label: x.name, val: Math.round(x.value * 100) / 100 }));
}

// Sort key = the range's lower bound, so buckets land on the x-axis in
// ascending numeric order regardless of each bucket's value.
function rangeSortKey(range: string): number {
  if (range.startsWith('-')) return -Infinity;
  const lower = range.endsWith('-') ? range.slice(0, -1) : range.split('-')[0];
  return Number(lower) || 0;
}

export function buildFollowerReachability(items: { following_range: string; value: number }[]) {
  if (!items?.length) return null;
  return [...items]
    .sort((a, b) => b.value - a.value)
    .slice(0, 8)
    .sort((a, b) => rangeSortKey(a.following_range) - rangeSortKey(b.following_range))
    .map(x => ({ label: formatOpenEndedRange(x.following_range), val: Math.round(x.value * 100) / 100 }));
}

export function themesForChart(detailed: Record<string, unknown> | undefined) {
  const themes = (detailed?.themes as { name?: string; score?: number }[]) ?? [];
  return themes.map((t) => ({
    label: String(t.name ?? 'Theme').slice(0, 28),
    val: Math.min(100, Math.max(0, Number(t.score) || 0)),
  }));
}

export const CONTENT_TYPE_COLORS: Record<string, string> = {
  reels:    '#818cf8',
  video:    '#818cf8',
  image:    '#fb923c',
  carousel: '#34d399',
};

export interface ContentTypeSegment {
  label: string;
  pct: number;
  color: string;
}

export function buildContentTypeMix(
  stats: Array<{ content_type?: string; type?: string; value?: number }> | undefined,
): ContentTypeSegment[] | null {
  if (!stats?.length) return null;
  // Aggregate by normalised key (video → reels) so duplicate/aliased types merge.
  const totals = new Map<string, number>();
  for (const s of stats) {
    let raw = (s.content_type ?? s.type ?? '').toLowerCase();
    if (!raw || (s.value ?? 0) <= 0) continue;
    if (raw === 'video') raw = 'reels';
    totals.set(raw, (totals.get(raw) ?? 0) + (s.value ?? 0));
  }
  if (totals.size === 0) return null;
  return Array.from(totals.entries())
    .sort(([, a], [, b]) => b - a)
    .map(([key, value]): ContentTypeSegment => ({
      label: key.charAt(0).toUpperCase() + key.slice(1),
      pct: Math.round(value),
      color: CONTENT_TYPE_COLORS[key] ?? '#94a3b8',
    }));
}
